(function () {
  const DISMISS_KEY = 'floating_announcement_dismissed';

  function parseDismissList(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function getDismissList() {
    try {
      return parseDismissList(sessionStorage.getItem(DISMISS_KEY));
    } catch (e) {
      return [];
    }
  }

  function persistDismissHash(hash) {
    if (!hash) return;
    try {
      const set = new Set(getDismissList());
      set.add(hash);
      sessionStorage.setItem(DISMISS_KEY, Array.from(set).join(','));
    } catch (e) {
      // sessionStorage may be unavailable (private mode quotas, etc.)
    }
  }

  function isHashDismissed(hash) {
    if (!hash) return false;
    return getDismissList().includes(hash);
  }

  if (!window.customElements.get('floating-announcement')) {
    class FloatingAnnouncement extends HTMLElement {
      connectedCallback() {
        if (this.classList.contains('is-dismissed')) {
          return;
        }

        if (this.isDismissed()) {
          this.finalizeHide();
          return;
        }

        this._abortController = new AbortController();
        const { signal } = this._abortController;

        this.setupDismissButton(signal);
        this.setupAccessibility(signal);
        this.setupScrollReveal(signal);
      }

      disconnectedCallback() {
        this._abortController?.abort();
      }

      get enableDismiss() {
        const raw = this.getAttribute('data-enable-dismiss');
        if (raw === null) return true;
        const s = String(raw).trim().toLowerCase();
        if (s === '' || s === 'false' || s === '0' || s === 'no') return false;
        return true;
      }

      get contentHash() {
        return this.dataset.contentHash || '';
      }

      get isRevealed() {
        return this.classList.contains('is-visible');
      }

      setupDismissButton(signal) {
        if (!this.enableDismiss) return;

        this.addEventListener(
          'click',
          (e) => {
            const close = e.target.closest('[data-dismiss-announcement]');
            if (!close || !this.contains(close)) return;
            e.preventDefault();
            this.dismiss();
          },
          { signal },
        );
      }

      setupAccessibility(signal) {
        if (!this.enableDismiss) return;

        this.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
              this.dismiss();
            }
          },
          { signal },
        );
      }

      getFooterTop() {
        const footer = document.querySelector('footer');
        if (!footer) return Number.POSITIVE_INFINITY;
        return footer.getBoundingClientRect().top + window.scrollY;
      }

      shouldBeVisible() {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        const footerTop = this.getFooterTop();

        // Visible only between one viewport past the page top and the footer's top edge
        return scrollY >= windowHeight && scrollY < footerTop;
      }

      setupScrollReveal(signal) {
        // Theme editor: keep the bar visible so merchants can configure it
        if (window.Shopify?.designMode) {
          this.reveal();
          return;
        }

        const update = () => {
          if (this.classList.contains('is-dismissed')) return;

          if (this.shouldBeVisible()) {
            this.reveal();
          } else {
            this.conceal();
          }
        };

        update();
        window.addEventListener('scroll', update, { passive: true, signal });
        window.addEventListener('resize', update, { passive: true, signal });
        window.addEventListener('pageshow', update, { passive: true, signal });
      }

      reveal() {
        if (this.isRevealed || this.classList.contains('is-dismissed')) return;
        this.classList.add('is-visible');
        this.classList.remove('opacity-0', 'pointer-events-none');
        this.classList.add('opacity-100', 'pointer-events-auto');
        this.setAttribute('aria-hidden', 'false');
      }

      conceal() {
        if (!this.isRevealed || this.classList.contains('is-dismissed')) return;
        this.classList.remove('is-visible', 'opacity-100', 'pointer-events-auto');
        this.classList.add('opacity-0', 'pointer-events-none');
        this.setAttribute('aria-hidden', 'true');
      }

      dismiss() {
        if (this.isAnimatingDismiss || this.classList.contains('is-dismissed')) return;
        this.isAnimatingDismiss = true;
        this.saveDismissState();

        const styles = getComputedStyle(this);
        const startOpacity = parseFloat(styles.opacity);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canAnimate = startOpacity > 0 && !reducedMotion && styles.transitionDuration !== '0s';

        // Prevent scroll logic from re-showing while we fade out
        this.classList.add('is-dismissed');
        this.classList.remove('is-visible', 'opacity-100', 'pointer-events-auto');
        this.classList.add('opacity-0', 'pointer-events-none');
        this.setAttribute('aria-hidden', 'true');

        const finish = () => {
          if (this._dismissFinish) {
            this.removeEventListener('transitionend', this._dismissFinish);
            this._dismissFinish = null;
          }
          if (this._dismissTimeout) {
            clearTimeout(this._dismissTimeout);
            this._dismissTimeout = null;
          }
          this.finalizeHide();
        };

        if (!canAnimate) {
          finish();
          return;
        }

        // Force a reflow so the opacity transition reliably runs from the open state
        void this.offsetWidth;

        this._dismissFinish = (e) => {
          if (e.target !== this || e.propertyName !== 'opacity') return;
          finish();
        };
        this.addEventListener('transitionend', this._dismissFinish);

        // Fallback if transitionend never fires (matches duration-500 + buffer)
        this._dismissTimeout = setTimeout(finish, 600);
      }

      finalizeHide() {
        this.style.display = 'none';
        this.classList.add('is-dismissed');
        this.classList.remove('is-visible', 'opacity-100', 'pointer-events-auto');
        this.classList.add('opacity-0', 'pointer-events-none');
        this.setAttribute('aria-hidden', 'true');
        this.isAnimatingDismiss = false;
        document.dispatchEvent(new CustomEvent('floating-announcement:dismissed'));
      }

      isDismissed() {
        return isHashDismissed(this.contentHash);
      }

      saveDismissState() {
        persistDismissHash(this.contentHash);
      }
    }

    window.customElements.define('floating-announcement', FloatingAnnouncement);
  }
})();
